import { compileDefinitionInputs, locationTypeAmbience } from '../janus-engine/authoring/location-type-ambience';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { encounterDefaults } from '../janus-engine/authoring/encounter-defaults';

// encounter-ambience.ts — encounter ambience-audio row helpers (template.js cleanup roadmap T2.4, 2026-06-14).
//
// Encounter ambience audio-row controls: clear/apply one row (silent
// Tom Select + the two-stage AJAX subcategory/name dropdown refresh) and apply the location-type
// default ambience triplets (current location, or a chosen location_type) — carved out of template.js.
//
// Public surface (window-attached): clearEncounterAmbienceRow / applyEncounterAmbienceFromLocationTypeDefaults /
// applyEncounterAmbienceFromLocationTypeId (PHP onclick — tools/encounters/ft3d_scene_host.php),
// applyEncounterAmbienceRow (locations-init.js runtime caller + internal). All runtime-invoked.
//
// Loaded AFTER template.js; deps resolve at call time (onclick / load-defaults flow): BASE_URL
// (core-helpers), setTomSelectValue (tom-select-init.js), queryData2Input1 / queryData2Input2 /
// loadPostFunctions / checkIfValidUUID (template.js), modifyEncounter (side-panels-init.js).

/** Clears encounter ambience audio row: Tom Select + AJAX subcategory/name dropdowns, blanks all three, then optionally saves encounter. */
async function clearEncounterAmbienceRow(prefix: string, rootUrl?: string, options: { persist?: boolean; current?: () => boolean } = {}): Promise<void> {
    const persist = options.persist !== false;
    const current = options.current ?? (() => true);
    if (!current()) return;
    const base = rootUrl
        ? (rootUrl.endsWith('/') ? rootUrl : rootUrl + '/')
        : (typeof BASE_URL !== 'undefined' ? BASE_URL : '/');
    setTomSelectValue(prefix + '_category', '', { silent: true });
    await queryData2Input1(
        base + 'options/forms/select_query_audio_subcategories_inc.php',
        'div_refresh_audio_subcategory_' + prefix,
        'amb',
        prefix,
        prefix + '_category'
    );
    if (!current()) return;
    setTomSelectValue(prefix + '_subcategory', '', { silent: true });
    await queryData2Input2(
        base + 'options/forms/select_query_audio_names_inc.php',
        'div_refresh_audio_names_' + prefix,
        'amb',
        prefix,
        prefix + '_category',
        prefix + '_subcategory'
    );
    if (!current()) return;
    setTomSelectValue(prefix + '_id', '', { silent: true });
    if (persist) {
        await modifyEncounter();
    }
}

/** Sets one ambience row from category / subcategory / audio id (silent Tom Select + AJAX; no per-field save). */
async function applyEncounterAmbienceRow(prefix: string, audioId: unknown, category: unknown, subcategory: unknown, base?: string, current: () => boolean = () => true): Promise<void> {
    if (!current()) return;
    const b =
        base ||
        (typeof BASE_URL !== 'undefined' ? BASE_URL : '/');
    const audio = audioId && String(audioId).trim() !== '' ? String(audioId).trim() : '';
    const cat = category != null && category !== undefined ? String(category) : '';
    const sub = subcategory != null && subcategory !== undefined ? String(subcategory) : '';
    if (!audio) {
        await clearEncounterAmbienceRow(prefix, b, { persist: false, current });
        return;
    }
    setTomSelectValue(prefix + '_category', cat, { silent: true });
    await queryData2Input1(
        b + 'options/forms/select_query_audio_subcategories_inc.php',
        'div_refresh_audio_subcategory_' + prefix,
        'amb',
        prefix,
        prefix + '_category'
    );
    if (!current()) return;
    setTomSelectValue(prefix + '_subcategory', sub, { silent: true });
    await queryData2Input2(
        b + 'options/forms/select_query_audio_names_inc.php',
        'div_refresh_audio_names_' + prefix,
        'amb',
        prefix,
        prefix + '_category',
        prefix + '_subcategory'
    );
    if (!current()) return;
    setTomSelectValue(prefix + '_id', audio, { silent: true });
}

let ambienceSequence = 0;
let ambienceApplyQueue: Promise<void> = Promise.resolve();
function settleAmbienceRows(slots: Array<{ prefix: string; id: unknown; cat: unknown; sub: unknown }>, base: string, current: () => boolean): Promise<void> {
    const job = ambienceApplyQueue.catch(() => undefined).then(async () => {
        if (!current()) return;
        for (const slot of slots) {
            await applyEncounterAmbienceRow(slot.prefix, slot.id, slot.cat, slot.sub, base, current);
            if (!current()) return;
        }
        await loadPostFunctions();
        if (current()) await modifyEncounter();
    });
    ambienceApplyQueue = job;
    return job;
}

/** Apply the current location's native defaults, then save once. */
async function applyEncounterAmbienceFromLocationTypeDefaults(rootUrl?: string): Promise<void> {
    const sequence = ++ambienceSequence;
    const master = document.getElementById('encounter_master_parent_id') as HTMLInputElement | null;
    const location = document.getElementById('encounter_location_id') as HTMLInputElement | null;
    const encounter_master_parent_id = master?.value ?? '', encounter_location_id = location?.value ?? '';
    const current = () => sequence === ambienceSequence && document.getElementById('encounter_location_id') === location
        && master?.value === encounter_master_parent_id && location?.value === encounter_location_id;
    const base = rootUrl ? (rootUrl.endsWith('/') ? rootUrl : rootUrl + '/') : typeof BASE_URL !== 'undefined' ? BASE_URL : '/';
    if (!checkIfValidUUID(encounter_master_parent_id) || !checkIfValidUUID(encounter_location_id)) return;
    try {
        const response = await fetch(`${base}options/tools/encounters/load_encounter_defaults.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ encounter_master_parent_id, encounter_location_id }),
        });
        if (!response.ok) throw new Error('Encounter defaults unavailable. Please retry.');
        const raw: unknown = await response.json();
        if (!current()) return;
        const defaults = await encounterDefaults(raw, encounter_master_parent_id, encounter_location_id);
        if (current()) await settleAmbienceRows(defaults.ambience, base, current);
    } catch (error) {
        if (current()) displayWarning(error instanceof Error ? error.message : 'Encounter defaults unavailable. Please retry.');
    }
}

/** Apply a chosen location_type's default ambience IDs (and category/subcategory for UX) without touching the encounter's actual location, then save once. */
async function applyEncounterAmbienceFromLocationTypeId(locationTypeId: unknown, rootUrl?: string): Promise<void> {
    const sequence = ++ambienceSequence;
    const selector = document.querySelector<HTMLSelectElement>('[data-ft-encounter-change="ambience-location-type"]');
    const selection = selector?.value;
    const current = () => sequence === ambienceSequence && (!selector || selector.value === selection);
    const base = rootUrl ? (rootUrl.endsWith('/') ? rootUrl : rootUrl + '/') : typeof BASE_URL !== 'undefined' ? BASE_URL : '/';
    if (!checkIfValidUUID(locationTypeId)) return;
    try {
        const response = await fetch(`${base}options/tools/encounters/load_location_type_ambience_defaults.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location_type: locationTypeId }),
        });
        if (!response.ok) throw new Error('Ambience inputs unavailable. Please retry.');
        const raw: unknown = await response.json();
        if (!current()) return;
        if (!fromBaseRecord(raw) || String(raw.location_type).toUpperCase() !== String(locationTypeId).toUpperCase()
            || !Array.isArray(raw.audio) || !raw.audio.every(fromBaseRecord)) throw new Error('Ambience inputs incomplete. Please retry.');
        const definitions = await compileDefinitionInputs(raw, [String(locationTypeId)]);
        if (!current()) return;
        const type = definitions.get(String(locationTypeId).toUpperCase());
        if (!type) throw new Error('Location type unavailable. Please retry.');
        await settleAmbienceRows(locationTypeAmbience(type, raw.audio), base, current);
    } catch (error) {
        if (current()) displayWarning(error instanceof Error ? error.message : 'Ambience inputs unavailable. Please retry.');
    }
}

// ===========================================================================================
// Window attachment — the public surface the originals exposed. All runtime-invoked (PHP onclick
// in ft3d_scene_host.php + locations-init.js runtime caller of applyEncounterAmbienceRow).
// ===========================================================================================
declare global {
    interface Window {
        clearEncounterAmbienceRow: typeof clearEncounterAmbienceRow;
        applyEncounterAmbienceRow: typeof applyEncounterAmbienceRow;
        applyEncounterAmbienceFromLocationTypeDefaults: typeof applyEncounterAmbienceFromLocationTypeDefaults;
        applyEncounterAmbienceFromLocationTypeId: typeof applyEncounterAmbienceFromLocationTypeId;
    }
}

Object.assign(window, {
    clearEncounterAmbienceRow,
    applyEncounterAmbienceRow,
    applyEncounterAmbienceFromLocationTypeDefaults,
    applyEncounterAmbienceFromLocationTypeId,
});

export {};
